import { useEffect, useMemo, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import { suppliersApi } from '../../../procurement/purchasing/suppliersApi';
import { maintenanceCategoriesApi, maintenanceItemsApi } from '../buildingManagementApi';
import {
  RECURRENCE_LABEL,
  type MaintenanceItemDetail,
  type MaintenanceRecurrence,
} from '../buildingManagementTypes';
import { sampleMaintenanceItem } from '../utils/sampleBuildingManagement';

interface Option {
  id: string;
  name: string;
  /** A code the reader may search by and that is shown under the name — a property's code. */
  code?: string;
}

/**
 * Register or correct one maintenance item.
 *
 * **Four pickers, each fed by what this module's keys reach.** The category and contractor lists
 * come through implied reads scoped to create/update; the building list is this module's own
 * feed, so nothing in Logistics is granted to fill it; the employee list is `personnel.read`,
 * with names resolved in ONE batch for the page of ids shown.
 *
 * **No name field** (12 Oct 2026) — an item is named by its category and its building, so both
 * take a full row and the building is REQUIRED. A fresh database owes a property in Logistics'
 * real-property register first, which the index page's banner says.
 *
 * **An edit injects what the record already names** — a category, building or contractor deleted
 * since — so the field states what the record SAYS rather than a placeholder. The server refuses
 * only a fresh pick of a deleted row.
 */
export function MaintenanceItemModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: MaintenanceItemDetail | null;
  onClose: () => void;
  onSaved: (saved: MaintenanceItemDetail) => void;
}) {
  const toast = useToast();
  const canReadPeople = usePermission('personnel.read');
  const canReadSuppliers = usePermission('suppliers.read');

  // Seeded once, on mount — the parent mounts this only while it is open.
  const [categoryId, setCategoryId] = useState(editing?.categoryId ?? '');
  const [recurrence, setRecurrence] = useState(editing ? String(editing.recurrence) : '');
  const [dueDate, setDueDate] = useState(editing?.dueDate ?? '');
  const [buildingId, setBuildingId] = useState(editing?.realPropertyId ?? '');
  const [location, setLocation] = useState(editing?.location ?? '');
  const [personnelId, setPersonnelId] = useState(editing?.responsiblePersonnelId ?? '');
  const [supplierId, setSupplierId] = useState(editing?.supplierId ?? '');
  const [remarks, setRemarks] = useState(editing?.remarks ?? '');

  const [categories, setCategories] = useState<Option[]>([]);
  const [buildings, setBuildings] = useState<Option[]>([]);
  const [staff, setStaff] = useState<Option[]>([]);
  const [suppliers, setSuppliers] = useState<Option[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setLoadingCatalogs(true);

    /*
      Each feed fails SOFT and on its own — a picker that could not load leaves its field empty
      and the rest of the form usable. A failed picker load names no field, so it must not reach
      the save path's error state.
    */
    const quiet = <T,>(promise: Promise<T>, fallback: T) => promise.catch(() => fallback);

    Promise.all([
      quiet(maintenanceCategoriesApi.options(controller.signal), []),
      quiet(maintenanceItemsApi.buildings(controller.signal), []),
      canReadSuppliers ? quiet(suppliersApi.options(controller.signal), []) : Promise.resolve([]),
      canReadPeople ? quiet(personnelApi.list({ pageSize: 100 }, controller.signal), null) : Promise.resolve(null),
    ])
      .then(async ([categoryOptions, buildingOptions, supplierOptions, personnel]) => {
        if (controller.signal.aborted) return;

        setCategories(categoryOptions.map((c) => ({ id: c.id, name: c.name })));
        setBuildings(buildingOptions.map((b) => ({ id: b.id, name: b.name, code: b.propertyCode })));
        setSuppliers(supplierOptions.map((s) => ({ id: s.id, name: s.name })));

        if (!personnel) return;

        /*
          ONE batch resolve for the ids shown, never one request per row. A record above this
          reader's level arrives with no personId — that absence is the mask — and the option
          falls back to the employee number, which identifies the row without naming the person.
        */
        const names = await personsApi
          .resolve(
            personnel.items.map((p) => p.personId).filter((id): id is string => id != null),
            controller.signal,
          )
          .catch(() => []);

        if (controller.signal.aborted) return;

        const byPersonId = new Map(names.map((n) => [n.personId, n.displayName]));

        setStaff(
          personnel.items.map((p) => ({
            id: p.id,
            name: (p.personId != null ? byPersonId.get(p.personId) : undefined) ?? p.employeeNumber,
          })),
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCatalogs(false);
      });

    return () => controller.abort();
  }, [open, canReadPeople, canReadSuppliers]);

  /*
    WHAT THE RECORD ALREADY NAMES is offered even where the live catalog has dropped it, so an
    edit opens stating the record rather than a placeholder. Labelled from the record's own DTO.
  */
  const offeredCategories = useMemo(
    () => withRecorded(categories, editing?.categoryId, editing?.categoryName),
    [categories, editing],
  );
  const offeredBuildings = useMemo(
    () =>
      withRecorded(buildings, editing?.realPropertyId, editing?.buildingName ?? undefined, editing?.buildingCode ?? undefined),
    [buildings, editing],
  );
  const offeredSuppliers = useMemo(
    () => withRecorded(suppliers, editing?.supplierId, editing?.supplierName ?? undefined),
    [suppliers, editing],
  );

  const valid = categoryId !== '' && buildingId !== '' && recurrence !== '' && dueDate !== '';
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      categoryId: categoryId || null,
      realPropertyId: buildingId || null,
      location: location.trim() || null,
      recurrence: recurrence === '' ? null : (Number(recurrence) as MaintenanceRecurrence),
      dueDate: dueDate || null,
      responsiblePersonnelId: personnelId || null,
      supplierId: supplierId || null,
      remarks: remarks.trim() || null,
    };

    try {
      const saved = editing
        ? await maintenanceItemsApi.update(editing.id, request)
        : await maintenanceItemsApi.create(request);

      toast.success(editing ? `Saved ${saved.referenceNumber}.` : `Registered ${saved.referenceNumber}.`);
      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the item.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'maintenance-item' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.referenceNumber}` : 'New maintenance item'}
      size="xl"
      id="maintenance-item-modal"
      loading={loadingCatalogs}
      autogenerate={{
        onGenerate: () => {
          const sample = sampleMaintenanceItem({
            categoryIds: categories.map((c) => c.id),
            buildingIds: buildings.map((b) => b.id),
            personnelIds: staff.map((s) => s.id),
            supplierIds: suppliers.map((s) => s.id),
          });

          setCategoryId(sample.categoryId ?? '');
          setRecurrence(sample.recurrence === null ? '' : String(sample.recurrence));
          setDueDate(sample.dueDate ?? '');
          setBuildingId(sample.realPropertyId ?? '');
          setLocation(sample.location ?? '');
          setPersonnelId(sample.responsiblePersonnelId ?? '');
          setSupplierId(sample.supplierId ?? '');
          setRemarks(sample.remarks ?? '');
        },
        disabled: saving || loadingCatalogs,
      }}
      confirm={{
        id: 'maintenance-item-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="maintenance-item-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="maintenance-item-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          CATEGORY and BUILDING take a full row each — together they are what the item is CALLED,
          so they lead the form rather than sitting as two halves of a grid row.
        */}
        <Select
          id="maintenance-item-category-id"
          label="Category"
          required
          description="What kind of obligation this is. A category may carry its own Near Expiration window."
          placeholder="Choose a category"
          value={categoryId}
          onChange={setCategoryId}
          options={offeredCategories.map((c) => ({ value: c.id, label: c.name }))}
          remote
          loading={loadingCatalogs}
          disabled={saving}
          error={error?.fieldError('categoryId')}
        />

        <Select
          id="maintenance-item-real-property-id"
          label="Building"
          required
          description="The building it belongs to, from Inventory › Real Property."
          placeholder="Choose a building"
          value={buildingId}
          onChange={setBuildingId}
          options={offeredBuildings.map((b) => ({
            value: b.id,
            label: b.name,
            sublabel: b.code,
            keywords: b.code,
          }))}
          remote
          loading={loadingCatalogs}
          disabled={saving}
          error={error?.fieldError('realPropertyId')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="maintenance-item-recurrence"
            label="Recurrence"
            required
            description="How often it falls due. Recording a maintenance suggests the next due date one period on."
            placeholder="Choose how often"
            value={recurrence}
            onChange={setRecurrence}
            sort="none"
            options={Object.entries(RECURRENCE_LABEL).map(([value, label]) => ({ value, label }))}
            disabled={saving}
            error={error?.fieldError('recurrence')}
          />

          <DateField
            id="maintenance-item-due-date"
            label="Due date"
            required
            description="The day it next falls due — a permit's expiry, or the next service. Every aging band is measured from it."
            value={dueDate}
            onChange={setDueDate}
            disabled={saving}
            error={error?.fieldError('dueDate')}
          />

          <TextField
            id="maintenance-item-location"
            label="Location"
            description="Where in the building, e.g. Roof deck or Basement plant room."
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={200}
            disabled={saving}
            error={error?.fieldError('location')}
          />

          {/*
            GATED ON THE KEY THAT FEEDS IT, and it degrades rather than lying: without
            `personnel.read` the picker would be empty and say nothing about why, so it is not
            drawn — and an edit sends the stored id back untouched, because the state was seeded
            from the record.
          */}
          {canReadPeople ? (
            <Select
              id="maintenance-item-responsible-personnel-id"
              label="Responsible employee"
              description="Who answers for this item. The name is read from the personnel file, never stored here."
              placeholder="Choose an employee"
              value={personnelId}
              onChange={setPersonnelId}
              options={staff.map((s) => ({ value: s.id, label: s.name }))}
              remote
              loading={loadingCatalogs}
              disabled={saving}
              error={error?.fieldError('responsiblePersonnelId')}
            />
          ) : null}

        </div>

        {/* FULL WIDTH (product call, 12 Oct 2026) — a contractor's name runs long. */}
        {canReadSuppliers ? (
          <Select
            id="maintenance-item-supplier-id"
            label="Contractor"
            description="Who does the work, from the supplier registry."
            placeholder="Choose a contractor"
            value={supplierId}
            onChange={setSupplierId}
            options={offeredSuppliers.map((s) => ({ value: s.id, label: s.name }))}
            remote
            loading={loadingCatalogs}
            disabled={saving}
            error={error?.fieldError('supplierId')}
          />
        ) : null}

        {/* FULL WIDTH and outside the grid — a remark is a note about the record as a whole. */}
        <Textarea
          id="maintenance-item-remarks"
          label="Remarks"
          description="Anything worth knowing before the work is due."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          maxLength={2000}
          disabled={saving}
          error={error?.fieldError('remarks')}
        />
      </div>
    </Modal>
  );
}

/** The live options, plus the one the record already names if the catalog has dropped it. */
function withRecorded(
  options: Option[],
  id: string | null | undefined,
  name: string | undefined,
  code?: string,
): Option[] {
  if (id == null || name == null || options.some((o) => o.id === id)) return options;

  return [{ id, name, code }, ...options];
}
